"use client";

// ===========================================
// konakla - Turizm Belge Kartı (numara + kopyala)
// ===========================================
//
// NEDEN AYRI BİR BİLEŞEN:
//
// 1. Kart ilan detayında İKİ KEZ render ediliyordu (masaüstü sütunu ve mobil
//    akış) ve işaretleme kopyalanmıştı. Kopyalama butonu iki yere de gerektiği
//    için ortak bir bileşen artık kaçınılmazdı.
//
// 2. ⚠️ BUTON ESKİ YAPIYA EKLENEMEZDİ. Kartın tamamı bir `<a>` idi; HTML'de bir
//    bağlantının içine buton koymak geçersizdir (interaktif içerik iç içe
//    geçemez) ve tarayıcılar bunu öngörülemez şekilde düzeltir — kopyalama
//    tıklaması Bakanlık sayfasını da açardı. Bu yüzden kap `<div>` oldu;
//    bağlantı ile buton artık KARDEŞ.
//
// Davranış değişikliği (bilerek): eskiden kartın tamamı hover'da renk
// değiştiriyordu, yani "her yeri tıklanabilir" izlenimi veriyordu. Artık
// yalnızca bağlantı metni altı çizilerek vurgulanıyor; kopyalama butonunun
// kendi hover'ı var. Neyin nereye götürdüğü böylece dürüst görünüyor.

import { useState } from "react";
import Image from "next/image";
import { Check, Copy } from "lucide-react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";

/** T.C. Kültür ve Turizm Bakanlığı belgeli konut sorgulama sayfası. */
const BAKANLIK_SORGULAMA_URL = "https://www.ktb.gov.tr/genel/bakanlikbelgelikonutlar.aspx";

interface Props {
  belgeNo: string;
  /** Mobil akıştaki dar yerleşim. */
  kompakt?: boolean;
  className?: string;
}

export function TurizmBelgeKarti({ belgeNo, kompakt = false, className }: Props) {
  const [kopyalandi, setKopyalandi] = useState(false);

  const kopyala = async () => {
    try {
      await navigator.clipboard.writeText(belgeNo);
      setKopyalandi(true);
      toast.success("Belge numarası kopyalandı");
      // İkon geçici olarak onay tikine döner; buton metni okuyucular için
      // aria-label ile sabit kalır.
      setTimeout(() => setKopyalandi(false), 2000);
    } catch {
      toast.error("Belge numarası kopyalanamadı");
    }
  };

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-lg border bg-white",
        kompakt ? "p-3" : "p-4",
        className
      )}
    >
      <a
        href={BAKANLIK_SORGULAMA_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="group flex min-w-0 flex-1 items-center gap-3"
      >
        <Image
          src="/turizm_bakanlik.png"
          alt="T.C. Kültür ve Turizm Bakanlığı"
          width={kompakt ? 24 : 32}
          height={kompakt ? 24 : 32}
          className={cn("shrink-0 object-contain", kompakt ? "h-6 w-6" : "h-8 w-8")}
        />
        <span
          className={cn(
            "text-foreground min-w-0 font-medium group-hover:underline",
            kompakt ? "text-xs" : "text-sm"
          )}
        >
          T.C. Kültür ve Turizm Bakanlığı Belge No: {belgeNo}
        </span>
      </a>

      <button
        type="button"
        onClick={kopyala}
        aria-label={`Belge numarasını kopyala: ${belgeNo}`}
        className={cn(
          "text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring",
          "flex shrink-0 items-center justify-center rounded-md transition-colors",
          "focus-visible:ring-2 focus-visible:outline-none",
          kompakt ? "h-7 w-7" : "h-8 w-8"
        )}
      >
        {kopyalandi ? (
          <Check className={cn("text-emerald-600", kompakt ? "h-3.5 w-3.5" : "h-4 w-4")} />
        ) : (
          <Copy className={cn(kompakt ? "h-3.5 w-3.5" : "h-4 w-4")} />
        )}
      </button>
    </div>
  );
}
